import {
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import { addEmailTemplate, resetStatus } from "@/store/slices/emailTemplateSlice";
import { useEffect } from "react";
import ReactQuill from 'react-quill-new';
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import ScrollToTop from "@/components/ScrollToTop";

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const getFormSchema = (t) =>
  Yup.object().shape({
    subject: Yup.string()
      .trim()
      .required(t("validations.subject"))
      .min(3, t("validations.subjectMin"))
      .max(255, t("validations.subjectMax"))
      .matches(notBlankRegex, t("validations.subjectWhitespace"))
      .matches(noHTMLRegex, t("validations.subjectHTML"))
      .matches(noCodeRegex, t("validations.subjectCode")),

    alias: Yup.string()
      .trim()
      .required(t("validations.alias"))
      .min(3, t("validations.aliasMin"))
      .max(255, t("validations.aliasMax"))
      .matches(notBlankRegex, t("validations.aliasWhitespace"))
      .matches(noHTMLRegex, t("validations.aliasHTML"))
      .matches(noCodeRegex, t("validations.aliasCode")),

    // message: Yup.string()
    //   .required(t("validations.message"))
    //   .min(5, t("validations.messageMin")),
    //   // .max(2000, "Email message must be at most 2000 characters"),
    message: Yup.string()
      .test(
        "not-empty",
        t("validations.message"),
        (value) => value && value.replace(/<[^>]*>/g, "").trim().length > 0
      )
      .test(
        "min-length",
        t("validations.messageMin"),
        (value) => value && value.replace(/<[^>]*>/g, "").trim().length >= 5
      ),

    // variables: Yup.string()
    //   .nullable()
    //   .max(255, t("validations.variablesMax"))
    //   .matches(noCodeRegex, t("validations.variablesCode")),
    variables: Yup.string()
      .trim()
      .required(t("validations.variables"))
      .max(255, t("validations.variablesMax"))
      .matches(noCodeRegex, t("validations.variablesCode")),
  });

export function AddEmailTemplate({ isEdit = false, isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation("email-template");
  const { t: ct } = useTranslation("common");

  const selectedEmailTemplate = useSelector((s) => s.emailTemplate.selected);
  const { loading } = useSelector((state) => state.emailTemplate);

  const initialForm = {
    subject: "",
    alias: "",
    message: "",
    variables: "",
  };

  const { register, handleSubmit, control, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(getFormSchema(t)),
    defaultValues: initialForm
  });

  useEffect(() => {
    if (isEdit) {
      if (selectedEmailTemplate && String(selectedEmailTemplate.id) === String(id)) {
        reset({ ...selectedEmailTemplate });
      }
    } else {
      reset(initialForm);
    }
  }, [isEdit, id, selectedEmailTemplate, dispatch]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addEmailTemplate(data)).unwrap();
      if (result.status) {
        Toaster.success(isEdit ? t('toaster.updateSuccess') : t('toaster.addSuccess'));
        dispatch(resetStatus());
        reset();
        navigate(`/dashboard/email-templates`);
      } else {
        Toaster.error(result.message || t("toaster.somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("toaster.addFailed"));
      dispatch(resetStatus());
    }
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? t('viewEmailTemplate') : isEdit ? t('editEmailTemplate') : t('addEmailTemplate')}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/email-templates`)}
                  className="rounded-lg"
                >
                  {ct('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                  {loading ? ct("saving") : isEdit ? ct("update") : ct("save")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/email-templates`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {ct('back')}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t('emailTemplateDetails')}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t('emailSubject')} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t('emailSubjectPlaceholder')}
                  {...register('subject')}
                  error={Boolean(errors.subject)}
                />
                {errors.subject && (
                  <p className="text-red-500 text-sm mt-1">{errors.subject.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t('emailAlias')} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t('emailAliasPlaceholder')}
                  {...register('alias')}
                  error={Boolean(errors.alias)}
                />
                {errors.alias && (
                  <p className="text-red-500 text-sm mt-1">{errors.alias.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t('emailVariables')} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t('emailVariablesPlaceholder')}
                  {...register('variables')}
                  error={Boolean(errors.variables)}
                />
                {errors.variables && (
                  <p className="text-red-500 text-sm mt-1">{errors.variables.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t('emailMessage')} <span className="text-red-500">*</span>
                </Typography>
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      theme="snow"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('emailMessagePlaceholder')}
                      // className="h-48 mb-5"
                      readOnly={isView}
                    />
                  )}
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddEmailTemplate;
